import { format, formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import { AlertTriangle, Loader2, Send, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { trpc } from '~/api/trpc';
import { CustomerAppointmentHistory } from '~/components/customer/CustomerAppointmentHistory';
import { hasAppointmentEnded } from '~/lib/cancellation-fee-config';
import { useUserStore } from '~/stores/user-store';
import { UserRole } from '~/types/server-types/UserRole';

type PendingFeeTier = { id: string; name: string; unitPriceGross: number };

type PendingFeeRow = {
    id: string;
    sendAfter: string | Date;
    lastError: string | null;
    markedBy: string | null;
    appointment: { id: string; title: string; startDate: string | Date | null; endDate: string | Date | null; durationMinutes: number };
    store: { id: number; name: string } | null;
    customer: { id: number; firstName: string; lastName: string; email: string | null };
    tier: PendingFeeTier | null;
    availableTiers: PendingFeeTier[];
    amountGross: number | null;
    cappedByCart: boolean;
    cartTotalGross: number | null;
};

type ConfirmAction = { type: 'send' | 'remove'; fee: PendingFeeRow };

const formatEuro = (amount: number) =>
    amount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

export const PendingCancellationFeesTable = () => {
    const navigate = useNavigate();
    const trpcUtils = trpc.useUtils();
    const { role } = useUserStore(
        useShallow((state) => ({ role: state.user?.role }))
    );
    const isAdmin = role === UserRole.ADMIN;

    const [selectedStoreId, setSelectedStoreId] = useState<string>(isAdmin ? 'all' : '');
    const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);

    const { data: storesData } = trpc.stores.getAll.useQuery({ filters: {} });
    const stores = useMemo(() => {
        const data = storesData?.data || [];
        return [...data].sort((a, b) => a.name.localeCompare(b.name));
    }, [storesData?.data]);

    useEffect(() => {
        if (isAdmin && selectedStoreId === '') {
            setSelectedStoreId('all');
        } else if (!isAdmin && stores.length > 0 && !selectedStoreId) {
            setSelectedStoreId(String(stores[0].id));
        }
    }, [stores, isAdmin, selectedStoreId]);

    const { data, isLoading } = trpc.cancellationFees.getPendingFees.useQuery(
        { storeId: selectedStoreId !== 'all' && selectedStoreId !== '' ? parseInt(selectedStoreId) : undefined },
        { enabled: selectedStoreId !== '' }
    );
    const pendingFees = data as PendingFeeRow[] | undefined;

    const invalidate = () => {
        trpcUtils.cancellationFees.getPendingFees.invalidate();
        trpcUtils.cancellationFees.getTodaysAppointments.invalidate();
        trpcUtils.billingDrafts.getAll.invalidate();
    };

    const updateTierMutation = trpc.cancellationFees.updatePendingFeeTier.useMutation({
        onSuccess: () => {
            toast.success('Gebührenstufe geändert');
            invalidate();
        },
        onError: (error) => toast.error(error.message),
    });

    const removeMutation = trpc.cancellationFees.removePendingFee.useMutation({
        onSuccess: () => {
            toast.success('Ausfallentschädigung entfernt – es wird nichts versendet');
            setConfirmAction(null);
            invalidate();
        },
        onError: (error) => toast.error(error.message),
    });

    const sendNowMutation = trpc.cancellationFees.sendPendingFeeNow.useMutation({
        onSuccess: (result: { sent: boolean; billingDraftNumber: string; email: string; warning: string | null }) => {
            if (result.sent) {
                toast.success(`Zahlungslink für Ausfallentschädigung ${result.billingDraftNumber} wurde an ${result.email} gesendet`);
            } else {
                toast.warning(result.warning ?? 'Der Zahlungslink konnte nicht versendet werden');
            }
            setConfirmAction(null);
            invalidate();
        },
        onError: (error) => {
            toast.error(error.message || 'Ausfallentschädigung konnte nicht versendet werden');
            setConfirmAction(null);
            invalidate();
        },
    });

    const isConfirming = removeMutation.isPending || sendNowMutation.isPending;

    const onConfirm = () => {
        if (!confirmAction) return;
        if (confirmAction.type === 'send') {
            sendNowMutation.mutate({ id: confirmAction.fee.id });
        } else {
            removeMutation.mutate({ id: confirmAction.fee.id });
        }
    };

    return (
        <>
            <div className='flex items-center justify-between gap-4 mb-4'>
                <p className='text-sm text-muted-foreground'>
                    Nicht wahrgenommene Termine erhalten automatisch nach 48 Stunden eine Ausfallentschädigung per Zahlungslink.
                    Bis dahin kann es entfernt oder die Gebührenstufe geändert werden.
                </p>
                <Select value={selectedStoreId} onValueChange={setSelectedStoreId}>
                    <SelectTrigger className='w-[200px] shrink-0'>
                        <SelectValue placeholder='Alle Filialen' />
                    </SelectTrigger>
                    <SelectContent>
                        {isAdmin && <SelectItem value='all'>Alle Filialen</SelectItem>}
                        {stores.map((store) => (
                            <SelectItem key={store.id} value={String(store.id)}>
                                {store.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <Card>
                <CardContent className='p-0'>
                    {isLoading || selectedStoreId === '' ? (
                        <div className='p-6 space-y-4'>
                            {[...Array(3)].map((_, i) => (
                                <Skeleton key={i} className='h-12 w-full' />
                            ))}
                        </div>
                    ) : (
                        <Table className='text-sm'>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Kunde</TableHead>
                                    <TableHead>Empfänger</TableHead>
                                    <TableHead>Termin</TableHead>
                                    <TableHead>Filiale</TableHead>
                                    <TableHead>Gebührenstufe</TableHead>
                                    <TableHead>Betrag</TableHead>
                                    <TableHead>Wird gesendet</TableHead>
                                    <TableHead />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {pendingFees && pendingFees.length > 0 ? (
                                    pendingFees.map((fee) => {
                                        const sendAfter = new Date(fee.sendAfter);
                                        const isDue = sendAfter.getTime() <= Date.now();
                                        const hasEnded = hasAppointmentEnded(fee.appointment.endDate);
                                        const canSend = !!fee.tier && !!fee.customer.email && hasEnded;
                                        const sendDisabledReason = !hasEnded
                                            ? 'Eine Ausfallentschädigung kann erst nach Terminende versendet werden'
                                            : 'Gebührenstufe und E-Mail-Adresse erforderlich';
                                        return (
                                            <TableRow key={fee.id}>
                                                <TableCell className='align-top'>
                                                    <div
                                                        className='font-medium cursor-pointer hover:underline text-primary'
                                                        onClick={() => navigate(`/customer/${fee.customer.id}`)}
                                                    >
                                                        {fee.customer.firstName} {fee.customer.lastName}
                                                    </div>
                                                    {fee.markedBy && (
                                                        <div className='text-muted-foreground'>Markiert von {fee.markedBy}</div>
                                                    )}
                                                    <div className='mt-1'>
                                                        <CustomerAppointmentHistory customerId={fee.customer.id} buttonOnly />
                                                    </div>
                                                </TableCell>
                                                <TableCell className='align-top'>
                                                    {fee.customer.email ? (
                                                        <span className='break-all'>{fee.customer.email}</span>
                                                    ) : (
                                                        <Badge variant='destructive'>Keine E-Mail-Adresse</Badge>
                                                    )}
                                                </TableCell>
                                                <TableCell className='align-top'>
                                                    <div className='font-medium whitespace-nowrap'>
                                                        {fee.appointment.startDate
                                                            ? format(new Date(fee.appointment.startDate), "dd.MM.yyyy HH:mm 'Uhr'")
                                                            : '–'}
                                                    </div>
                                                    <div className='text-muted-foreground line-clamp-1' title={fee.appointment.title}>
                                                        {fee.appointment.durationMinutes} Min. · {fee.appointment.title}
                                                    </div>
                                                </TableCell>
                                                <TableCell className='align-top'>{fee.store?.name ?? '–'}</TableCell>
                                                <TableCell className='align-top min-w-[220px]'>
                                                    <Select
                                                        value={fee.tier?.id ?? ''}
                                                        onValueChange={(tierId) => updateTierMutation.mutate({ id: fee.id, tierId })}
                                                        disabled={updateTierMutation.isPending}
                                                    >
                                                        <SelectTrigger className={fee.tier ? '' : 'border-destructive'}>
                                                            <SelectValue placeholder='Gebührenstufe wählen' />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {fee.availableTiers.map((tier) => (
                                                                <SelectItem key={tier.id} value={tier.id}>
                                                                    {tier.name} ({formatEuro(tier.unitPriceGross)})
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    {fee.lastError && (
                                                        <div className='mt-2 flex items-start gap-1 text-destructive'>
                                                            <AlertTriangle className='h-4 w-4 shrink-0 mt-0.5' />
                                                            <span>{fee.lastError}</span>
                                                        </div>
                                                    )}
                                                </TableCell>
                                                <TableCell className='align-top whitespace-nowrap'>
                                                    <div>{fee.amountGross !== null ? formatEuro(fee.amountGross) : '–'}</div>
                                                    {fee.cappedByCart && (
                                                        <div className='text-muted-foreground'>Begrenzt auf Warenkorbwert</div>
                                                    )}
                                                </TableCell>
                                                <TableCell className='align-top whitespace-nowrap'>
                                                    <div>{format(sendAfter, "dd.MM.yyyy HH:mm 'Uhr'")}</div>
                                                    <div className='text-muted-foreground'>
                                                        {isDue
                                                            ? 'bei der nächsten Ausführung'
                                                            : `in ${formatDistanceToNow(sendAfter, { locale: de })}`}
                                                    </div>
                                                </TableCell>
                                                <TableCell className='align-top text-right'>
                                                    <div className='flex justify-end gap-2'>
                                                        <Button
                                                            size='sm'
                                                            disabled={!canSend}
                                                            title={canSend ? undefined : sendDisabledReason}
                                                            onClick={() => setConfirmAction({ type: 'send', fee })}
                                                        >
                                                            <Send className='mr-1 h-4 w-4' />
                                                            Jetzt senden
                                                        </Button>
                                                        <Button
                                                            size='sm'
                                                            variant='outline'
                                                            onClick={() => setConfirmAction({ type: 'remove', fee })}
                                                        >
                                                            <Trash2 className='mr-1 h-4 w-4' />
                                                            Entfernen
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={8} className='h-24 text-center'>
                                            Keine geplanten Ausfallentschädigungen.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            <AlertDialog open={!!confirmAction} onOpenChange={(open) => !open && !isConfirming && setConfirmAction(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {confirmAction?.type === 'send' ? 'Ausfallentschädigung jetzt senden?' : 'Ausfallentschädigung entfernen?'}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {confirmAction?.type === 'send'
                                ? `Ausfallentschädigung über ${confirmAction.fee.amountGross !== null ? formatEuro(confirmAction.fee.amountGross) : '–'} jetzt an ${confirmAction.fee.customer.email} senden? Der Auftrag und die Rechnung werden sofort erstellt.`
                                : `Für ${confirmAction?.fee.customer.firstName} ${confirmAction?.fee.customer.lastName} wird keine Ausfallentschädigung erstellt oder versendet.`}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isConfirming}>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            disabled={isConfirming}
                            onClick={(event) => {
                                event.preventDefault();
                                onConfirm();
                            }}
                        >
                            {isConfirming && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                            {confirmAction?.type === 'send' ? 'Jetzt senden' : 'Entfernen'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};
